import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    TrendingUp,
    Target,
    Newspaper,
    Calendar,
    ChevronRight,
    Video,
    Play
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryInfo = {
    weekly_bias: { icon: TrendingUp, color: 'bg-blue-50 text-blue-600' },
    key_zones: { icon: Target, color: 'bg-purple-50 text-purple-600' },
    news_impact: { icon: Newspaper, color: 'bg-amber-50 text-amber-600' }
};

export const MarketInsightPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { t, language, getLocalizedField } = useLanguage();
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedInsight, setSelectedInsight] = useState(null);

    const currentCategory = searchParams.get('category') || 'all';

    useEffect(() => {
        fetchInsights();
    }, [currentCategory]);

    const fetchInsights = async () => {
        setLoading(true);
        try {
            const url = currentCategory === 'all' 
                ? `${API}/market-insights`
                : `${API}/market-insights?category=${currentCategory}`;
            const response = await axios.get(url);
            setInsights(response.data);
        } catch (error) {
            console.error('Error fetching insights:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryChange = (category) => {
        if (category === 'all') {
            searchParams.delete('category');
        } else {
            searchParams.set('category', category);
        }
        setSearchParams(searchParams);
        setSelectedInsight(null);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-12 w-full" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} className="h-48" />
                        ))}
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div>
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-slate-900">
                        {t('marketInsight')}
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {language === 'id' 
                            ? 'Analisa market dan insight trading mingguan'
                            : 'Weekly market analysis and trading insights'
                        }
                    </p>
                </div>

                {/* Category Tabs */}
                <Tabs value={currentCategory} onValueChange={handleCategoryChange}>
                    <TabsList className="w-full md:w-auto grid grid-cols-4 md:flex">
                        <TabsTrigger value="all" data-testid="tab-all">
                            {language === 'id' ? 'Semua' : 'All'}
                        </TabsTrigger>
                        <TabsTrigger value="weekly_bias" data-testid="tab-weekly-bias">
                            {t('weeklyBias')}
                        </TabsTrigger>
                        <TabsTrigger value="key_zones" data-testid="tab-key-zones">
                            {t('keyZones')}
                        </TabsTrigger>
                        <TabsTrigger value="news_impact" data-testid="tab-news-impact">
                            {t('newsImpact')}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value={currentCategory} className="mt-6">
                        {selectedInsight ? (
                            // Detail View
                            <Card data-testid="insight-detail">
                                <CardContent className="pt-6">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="mb-4"
                                        onClick={() => setSelectedInsight(null)}
                                    >
                                        <ChevronRight className="w-4 h-4 mr-1 rotate-180" />
                                        {t('back')}
                                    </Button>

                                    <div className="flex items-center gap-3 mb-4">
                                        {(() => {
                                            const info = categoryInfo[selectedInsight.category];
                                            const Icon = info?.icon || TrendingUp;
                                            return (
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${info?.color || 'bg-slate-100'}`}>
                                                    <Icon className="w-5 h-5" />
                                                </div>
                                            );
                                        })()}
                                        <div>
                                            <h2 className="text-xl font-heading font-bold text-slate-900">
                                                {getLocalizedField(selectedInsight, 'title')}
                                            </h2>
                                            <p className="text-sm text-slate-500 flex items-center gap-1">
                                                <Calendar className="w-3.5 h-3.5" />
                                                {formatDate(selectedInsight.published_at)}
                                            </p>
                                        </div>
                                    </div>

                                    {selectedInsight.image_url && (
                                        <img
                                            src={selectedInsight.image_url}
                                            alt={getLocalizedField(selectedInsight, 'title')}
                                            className="w-full max-w-2xl rounded-lg mb-6"
                                        />
                                    )}

                                    {/* Video Player */}
                                    {selectedInsight.video_url && (
                                        <div className="mb-6">
                                            {selectedInsight.video_url.includes('youtube.com') ? (
                                                <div className="aspect-video rounded-xl overflow-hidden bg-slate-900 max-w-3xl">
                                                    <iframe
                                                        src={selectedInsight.video_url}
                                                        className="w-full h-full"
                                                        allowFullScreen
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                        title={getLocalizedField(selectedInsight, 'title')}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="aspect-video rounded-xl overflow-hidden bg-slate-900 max-w-3xl">
                                                    <video
                                                        src={selectedInsight.video_url}
                                                        controls
                                                        className="w-full h-full"
                                                        controlsList="nodownload"
                                                    >
                                                        Your browser does not support video playback.
                                                    </video>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="prose prose-slate max-w-none">
                                        <p className="whitespace-pre-wrap text-slate-600 leading-relaxed">
                                            {getLocalizedField(selectedInsight, 'content')}
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        ) : (
                            // List View
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {insights.length === 0 ? (
                                    <div className="col-span-2 text-center py-12">
                                        <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                        <p className="text-slate-500">
                                            {language === 'id' 
                                                ? 'Belum ada insight untuk kategori ini'
                                                : 'No insights available for this category'
                                            }
                                        </p>
                                    </div>
                                ) : (
                                    insights.map((insight) => {
                                        const info = categoryInfo[insight.category];
                                        const Icon = info?.icon || TrendingUp;
                                        const hasVideo = insight.video_url && insight.video_url.length > 0;

                                        return (
                                            <Card
                                                key={insight.id}
                                                className="card-hover cursor-pointer"
                                                onClick={() => setSelectedInsight(insight)}
                                                data-testid={`insight-card-${insight.id}`}
                                            >
                                                <CardContent className="pt-6">
                                                    <div className="flex items-start gap-4">
                                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${info?.color || 'bg-slate-100'}`}>
                                                            <Icon className="w-5 h-5" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <h3 className="font-heading font-semibold text-slate-900 line-clamp-2">
                                                                    {getLocalizedField(insight, 'title')}
                                                                </h3>
                                                                {hasVideo && (
                                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 text-red-600 text-xs">
                                                                        <Play className="w-3 h-3" />
                                                                        Video
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                                                                {getLocalizedField(insight, 'content')}
                                                            </p>
                                                            <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                                                                <Calendar className="w-3 h-3" />
                                                                {formatDate(insight.published_at)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
};
